// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { useEffect, useRef, useState } from "react";
import { t, useLocale } from "../i18n";

type Turnstile = {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      language: string;
      theme: string;
      size: string;
      callback(token: string): void;
      "expired-callback"(): void;
      "error-callback"(): void;
    },
  ): string;
  remove(id: string): void;
};
declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

export function TurnstileChallenge({
  siteKey,
  onToken,
}: {
  siteKey: string;
  onToken(token: string): void;
}) {
  const locale = useLocale();
  const container = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let stopped = false;
    let widget: string | undefined;
    onToken("");
    setFailed(false);
    const render = () => {
      if (
        stopped ||
        !container.current ||
        !window.turnstile ||
        widget !== undefined
      )
        return;
      try {
        widget = window.turnstile.render(container.current, {
          sitekey: siteKey,
          action: "guest_register",
          language: locale,
          theme: "dark",
          size: "flexible",
          callback: (token) => {
            if (!stopped) {
              onToken(token);
              setFailed(false);
            }
          },
          "expired-callback": () => {
            if (!stopped) onToken("");
          },
          "error-callback": () => {
            if (!stopped) {
              onToken("");
              setFailed(true);
            }
          },
        });
      } catch {
        setFailed(true);
      }
    };
    let script = document.querySelector<HTMLScriptElement>(
      "script[data-ranna-turnstile]",
    );
    if (!script) {
      script = document.createElement("script");
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.rannaTurnstile = "true";
      document.head.appendChild(script);
    }
    const failure = () => {
      if (!stopped) setFailed(true);
    };
    script.addEventListener("load", render);
    script.addEventListener("error", failure);
    render();
    const timeout = setTimeout(() => {
      if (widget === undefined) failure();
    }, 15000);
    return () => {
      stopped = true;
      clearTimeout(timeout);
      script.removeEventListener("load", render);
      script.removeEventListener("error", failure);
      if (widget !== undefined) window.turnstile?.remove(widget);
      onToken("");
    };
  }, [siteKey, locale, onToken, attempt]);
  return (
    <div className="registration-check">
      <div ref={container} aria-label={t("التحقق من أنك شخص حقيقي")} />
      {failed && (
        <p className="form-error" role="alert">
          {t("تعذر تحميل التحقق. حاول مجددًا.")}{" "}
          <button
            type="button"
            className="quiet-button"
            onClick={() => {
              if (!window.turnstile)
                document
                  .querySelector("script[data-ranna-turnstile]")
                  ?.remove();
              setAttempt((n) => n + 1);
            }}
          >
            {t("إعادة المحاولة")}
          </button>
        </p>
      )}
    </div>
  );
}
